import React, { useMemo, useState, ReactNode } from 'react';
import styled from '@emotion/styled';
import { useProfile, useUser } from 'common/CacheProvider';
import { Link, Button, Placeholder } from 'common/styles';
import {
  IconFilledBookmark,
  IconUser,
  IconSubscription,
} from 'common/icons';
import Menu from 'common/Menu';
import ProfileMenu from 'common/ProfileMenu';
import { __t } from 'common/i18n';
import { useTheme } from '@emotion/react';
import type { Theme } from '@emotion/react';
import { useRouter } from 'next/router';
import SubscriptionTooltip from 'common/SubscriptionTooltip';
import IconUserSetting from '../icons/IconUserSetting';
import UserAvatar from './UserAvatar';
import { getSubscriptionButtonMessage, replacePhoneNumberPrefix } from '../utils';

const StyledMenu = styled(Menu)(() => ({ width: '18.75rem' }));

const getAvatarControlStyles = (theme: Theme) => ({
  display: 'block' as const,
  cursor: 'pointer' as const,
  padding: 0,
  border: 'none',
  background: 'transparent',
  fontSize: theme.spacing(4),
  borderRadius: '10rem',
  overflow: 'hidden' as const,
  position: 'relative' as const,
});

const AvatarButton = styled.button(({ theme }) => getAvatarControlStyles(theme));
const AvatarLink = styled(Link)(({ theme }) => getAvatarControlStyles(theme));
type linkType = { label: string, icon: ReactNode, href: string, is_sso?: boolean };

export default function UserMenu({ scrollToTop }: { scrollToTop?: boolean }) {
  const { user, hasSubscription, loaded, subscriptionRemainingDays, carrierSubscription } = useUser();
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const { profile } = useProfile();

  const subscriptionButtonMessage = getSubscriptionButtonMessage(user, subscriptionRemainingDays);

  // Derived instead of kept in state, so the label and the is_sso flag follow every user
  // revalidation (the user restored from the cache on load can be stale) and the order of the
  // items no longer depends on whether the profile or the user arrived first.
  const links = useMemo<linkType[]>(() => {
    const items: linkType[] = [];
    if (loaded && user) {
      items.push({
        label: subscriptionButtonMessage,
        icon: <IconSubscription />,
        href: '/packages',
        is_sso: user.is_sso_login,
      });
    }
    if (profile && String(profile.isPrimary) === 'true') {
      items.push({
        label: 'profile.edit.profiles',
        icon: <IconUserSetting />,
        href: '/profile/choose',
        // href: `/profile/choose?redirect=${router.asPath}`,
      });
    }
    items.push({
      label: 'profile.bookmarks',
      icon: <IconFilledBookmark />,
      href: '/user/bookmarks',
    }, {
      label: 'profile.title',
      icon: <IconUser />,
      href: '/user',
    });
    return items;
  }, [loaded, user, profile, subscriptionButtonMessage]);

  const router = useRouter();
  const loginUrl = `/login?redirect=${router.asPath}`;
  const { breakpoints } = useTheme();

  const handleClick = () => {
    if (window.innerWidth < breakpoints.md) {
      router.push('/user/menu');
    } else {
      setOpen((prev) => !prev);
    }
  };

  if (!loaded) return <Loading redirect={loginUrl} />;
  if (!user) return <LoginButton redirect={loginUrl} />;
  const showPhoneTooltip = (router.pathname === '/packages' || router.pathname.includes('/tickets')) && user && user.msisdn;

  const handleShowTooltip = (subscribe, days): boolean => {
    if (subscribe) {
      if (days === 0) {
        return true;
      } else if (days > 0 && days <= 5) {
        return true;
      }
      return false;
    }
    return true;
  };
  return (
    <>
      <div style={{ position: 'relative' }}>
        <AvatarButton
          type="button"
          ref={setReferenceElement}
          onClick={handleClick}
          aria-label={__t('profile.title')}
        >
          <UserAvatar user={user} size="small" />
        </AvatarButton>
        {showPhoneTooltip && <MsidnBox>{replacePhoneNumberPrefix(Number(user?.msisdn))}</MsidnBox>}
        {loaded && !carrierSubscription && !user.is_sso_login && handleShowTooltip(hasSubscription, subscriptionRemainingDays)
          ? <div style={{ transition: 'top 0.2s ease-in-out', left: '-12px', position: 'absolute', top: scrollToTop ? '-20px' : '32px' }}>
            <SubscriptionTooltip days={subscriptionRemainingDays} hasSubscription={hasSubscription}/>
          </div> : null}
      </div>
      <StyledMenu onClose={() => setOpen(false)} anchor={referenceElement} open={open} placement="bottom-end"
        wrapperStyles={{ zIndex: 1299 }} strategy="fixed"
      >
        <ProfileMenu user={user} links={links} showStatus isPopup />
      </StyledMenu>
    </>
  );
}

type LoadingButtonProps = { redirect: string };

const LoginButton = ({ redirect }: LoadingButtonProps) => (
  <Button variant="secondary" inputSize="small" as={Link} href={redirect}>
    {__t('auth.login')}
  </Button>
);

const Loading = ({ redirect }: LoadingButtonProps) => (
  <AvatarLink href={redirect} aria-label={__t('auth.login')}>
    <Placeholder width={1} />
  </AvatarLink>
);

const MsidnBox = styled.div(({ theme }) => ({
  order: 0,
  height: '32px',
  textAlign: 'center',
  fontSize: '0.875rem',
  position: 'absolute',
  backgroundColor: theme.rgba(theme.palette.success[900][0], 0.08),
  border: `1px solid ${theme.rgba(theme.palette.success[900][0], 0.5)}`,
  color: theme.palette.success[900][0],
  borderRadius: '0.25rem',
  display: 'flex',
  alignItems: 'center',
  width: '9rem',
  justifyContent: 'center',
  top: '-2px',
  left: '40px',
  fontWeight: 'bold',
  paddingTop: '4px',
}));
